import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import qrCodeIcon from 'assets/images/icons/qr-code.svg';
import AlertModal from 'components/common-components/AlertModal';
import FormButton from 'components/form-components/FormButton';
import FormLoadingButton from 'components/form-components/FormLoadingButton';
import { CallAPI } from 'actions/General';
import { importUserFileHeaders, importUserTableHeaders } from './utils';
import { useTranslation } from 'react-i18next';
import importIcon from 'assets/images/icons/import.svg';
import toast from 'react-hot-toast';
import Papa from 'papaparse';
import TableComponent from 'components/table-component/Table';

const AddUserByImport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [APIError, setAPIError] = useState('');
  const [alertType, setAlertType] = useState('');
  const [modifiedUserTableHeader, setModifiedUserTableHeader] = useState(() => [
    ...importUserTableHeaders,
  ]);

  const fileInputRef = useRef(null);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      setValues([]);
      setErrors([]);
      fileInputRef.current.click();
    }
  };

  const openSelectedFile = async (e) => {
    const file = e.target.files[0];
    if (file) {
      Papa.parse(file, {
        skipEmptyLines: true,
        complete: async (result) => {
          const fileData = result.data;
          const { isValid, dataErrors, fileError, data } = validateCsvData(fileData);
          if (!isValid) {
            if (fileError)
              toast(`Error: ${JSON.stringify(fileError)}`, {
                style: { borderRadius: '8px', background: 'red', color: '#fff' },
              });
            else setErrors(dataErrors);
            setLoading(false);
          } else {
            setValues(data);
          }
        },
        error: (err) => {
          console.log('ERROR', err);
          toast(`Error: in parsing`, {
            style: { borderRadius: '8px', background: 'red', color: '#fff' },
          });
          setLoading(false);
        },
      });
    }
  };
  const inviteUser = async (e) => {
    e.preventDefault();
    let response = await CallAPI(
      'IMPORT_USERS',
      { sendInvite: false, users: values },
      null,
      setLoading,
      setAPIError,
      null,
    );
    console.log(response);
    if (response.status) setAlertType('success');
    else {
      if (response.message === 'user already exist') {
        console.log(JSON.parse(response.error));
        const alreadyExistedUsers = JSON.parse(response.error);
        const alreadyExistedUsersPhoneNumbers = alreadyExistedUsers.map(
          (singleUser) => singleUser.mobileNumber,
        );
        let errorMessages = [];
        values.map((singleUser) => {
          if (alreadyExistedUsersPhoneNumbers.indexOf(singleUser.mobileNumber) > -1) {
            errorMessages.push(
              `${singleUser.firstName} ${singleUser.lastName} with phone number ${singleUser.mobileNumber} already exist in company`,
            );
          }
        });
        setErrors(errorMessages);
        setAPIError('');
      } else setAlertType('delete');
    }
  };

  const validateCsvData = (fileData) => {
    const errors = [];

    if (!Array.isArray(fileData) || fileData.length < 2) {
      return {
        isValid: false,
        fileError: 'CSV file is empty or invalid',
        dataErrors: [],
        data: [],
      };
    }

    const headers = fileData[0]; // first row
    const dataRows = fileData.slice(1);

    if (headers.includes('Personal ID Number')) {
      setModifiedUserTableHeader((prev) => [
        ...prev,
        {
          name: 'personalIdNumber',
          isSelected: true,
          displayName: 'table_header_personal_id_number',
          populateFunc: '',
          associatedKey: 'personalIdNumber',
          isFilterAble: false,
          type: 'number',
        },
      ]);
    }

    // Map header name -> column index
    const headerIndexMap = {};
    headers.forEach((header, index) => {
      headerIndexMap[header] = index;
    });
    console.log('headerIndexMap', headerIndexMap);
    // 1. Validate required columns exist
    importUserFileHeaders.forEach(({ label, isRequired }) => {
      if (isRequired && !(label in headerIndexMap)) {
        errors.push(`Missing required column: ${label}`);
      }
    });

    // Stop if headers are missing
    if (errors.length) {
      return {
        isValid: false,
        fileError: '',
        dataErrors: errors,
        data: [],
      };
    }

    // 2. Validate required column values
    dataRows.forEach((row, rowIndex) => {
      importUserFileHeaders.forEach(({ label, isRequired }) => {
        const colIndex = headerIndexMap[label];
        const value = row[colIndex];

        if (isRequired && (value === undefined || value === null || String(value).trim() === '')) {
          errors.push(`Row ${rowIndex + 2}: ${label} is required`);
        } else if (label === 'Country Code') {
          if (['92', '966', '20'].indexOf(value) === -1) {
            errors.push(`Row ${rowIndex + 2}: ${label} is not valid`);
          }
        } else if (label === 'Phone Number') {
          if (isNaN(value) || value.trim() === '') {
            errors.push(`Row ${rowIndex + 2}: ${label} is not a valid number`);
          }
        } else if (label === 'Personal ID Number' && value) {
          if (isNaN(value)) {
            errors.push(`Row ${rowIndex + 2}: ${label} is not valid`);
          }
        }
      });
    });

    if (errors.length > 0)
      return {
        isValid: errors.length === 0,
        fileError: '',
        dataErrors: errors,
        data: [],
      };
    const data = dataRows.map((row) => {
      const obj = {};

      importUserFileHeaders.forEach(({ label, key }) => {
        if (label === 'Personal ID Number' && !headers.includes('Personal ID Number')) {
          return;
        }
        const index = headerIndexMap[label];

        const value = row[index];
        obj[key] =
          value === undefined || value === null || String(value).trim() === '' ? null : value;
      });

      return obj;
    });
    return {
      isValid: true,
      fileError: '',
      dataErrors: errors,
      data,
    };
  };

  return (
    <div className='add-user-form-section'>
      <AlertModal
        show={alertType !== ''}
        showCloseButton={true}
        closeModal={() => setAlertType('')}
        headerIcon={qrCodeIcon}
        title={alertType === 'success' ? 'alert_success' : 'alert_error'}
        message={alertType === 'success' ? 'alert_imported_users' : APIError}
        variant={alertType}
        actionButtons={[
          {
            text: t('button_ok'),
            variant: 'green-1',
            onClick: () => {
              if (alertType === 'success') navigate('/users');
              else setAlertType('');
            },
          },
        ]}
      />
      <div className='space-4'></div>
      <div>
        <FormButton
          text='button_import'
          onClick={handleUploadClick}
          variant='green-2'
          icon={importIcon}
        />
        <input
          className='d-none'
          ref={fileInputRef}
          type='file'
          accept='.csv'
          onChange={openSelectedFile}
        />
      </div>
      <div className='space-4'></div>
      {errors && errors.length > 0 ? (
        <>
          <h3 className='theme-text-red-1 theme-font-inter-medium'>Errors</h3>
          <ul>
            {errors.map((singleError, index) => (
              <li className='theme-text-red-1 theme-font-inter-regular theme-size-1_1' key={index}>
                {singleError}
              </li>
            ))}
          </ul>
          <div className='space-4'></div>
        </>
      ) : values.length > 0 ? (
        <>
          <TableComponent
            header={modifiedUserTableHeader}
            data={values}
            filterEnabled={false}
            totalRecords={values.length}
            loader={false}
            props={{}}
            isFieldSelectionEnabled={false}
            isPaginationEnabled={false}
            isActionsEnabled={false}
          />
        </>
      ) : (
        <></>
      )}
      <div className='d-flex flex-column align-items-end gap-3 add-user-button-layout'>
        <div className='d-flex justify-content-start gap-3'>
          <FormButton text='button_cancel' variant='white-1' onClick={() => navigate('/users')} />
          <FormLoadingButton
            onClick={inviteUser}
            text='button_confirm'
            variant='green-1'
            loading={loading}
            disabled={values.length === 0}
          />
        </div>
      </div>
    </div>
  );
};

export default AddUserByImport;
